/** @jsx jsx */

import { useContext, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { motion } from 'framer-motion';
import { CarouselContext } from './Carousel';
import isAppleComputerInc from 'services/isAppleComputerInc';
import { ImageZoomContext } from 'components/lib/Image/ImageZoom';

const defaultBulletFormatting = `
ul {
  list-style-type: disc;
  ul {
    list-style-type: circle;
    ul {
      list-style-type: square;
    }
  }
}

ol {
  ul {
    list-style-type: circle;
    ul {
      list-style-type: square;
    }
  }
}

.chameleon-text > div {

  ul, ol {
    list-style-position: inside;
  }

  > ul, > ol {
    display: inline-block;
    margin-left: 0px;
  }
}
`;
const CarouselSlide = ({
  index,
  currentIndex,
  imgHeight,
  carouselTextColor,
  animation,
  slide,
  includeText,
  includeSlideIndicators,
}) => {
  const img = slide.props.children[0];
  const text = slide.props.children[1];

  const { hasAnimated } = useContext(CarouselContext)!;

  const slideRef = useRef<HTMLLIElement | null>(null);
  const ar = {};

  if (currentIndex !== 0 && currentIndex === index) {
    ar['role'] = 'alert';
  }

  let content = text?.props?.content;
  if (content === '<p data-type="paragraph"></p>') {
    content = false;
  }

  const textHasContent = window.dev() || content;
  const imageZoomContextValue = {
    overideStyles: {
      right: 5,
      bottom: 25,
    },
  };

  // isiOS()
  return isAppleComputerInc() ? (
    <li
      ref={slideRef}
      css={css`
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        width: 100%;
        list-style-type: none;

        opacity: ${currentIndex === index ? 1 : 0};
        transition: opacity 0.35s;

        transform: translateX(-${100 * currentIndex}%);
      `}
      {...ar}
      aria-hidden={currentIndex !== index}
    >
      <div
        css={css`
          height: ${imgHeight}px;
          padding: ${window.dev() ? '5px' : '0px'};
        `}
      >
        <ImageZoomContext.Provider value={imageZoomContextValue}>
          {img}
        </ImageZoomContext.Provider>
      </div>

      {includeText && (
        <div
          css={css`
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          padding: ${window.dev() ? '3px 5px' : '13px 7px'};
          word-break: break-word;
          * div {
            ${!window.dev() && `padding: 5px 10px;`}
            color: ${carouselTextColor};
          }
          text-align: center;
          ${defaultBulletFormatting}
          
          display: ${currentIndex !== index ? 'none' : ''}
        `}
        >
          {text}
        </div>
      )}
    </li>
  ) : (
    <motion.li
      ref={slideRef}
      css={css`
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        width: 100%;
        list-style-type: none;
      `}
      animate={{
        transform: `translateX(-${100 * currentIndex}%)`,
        opacity: currentIndex === index ? 1 : 0,
      }}
      transition={
        {
          natural: {
            duration: 0.35,
            type: 'spring',
            stiffness: 260 / 1.5,
            damping: 19,
          },
          smooth: {
            duration: 0.35,
          },
          none: {
            duration: 0,
          },
        }[animation]
      }
      onAnimationComplete={() => {
        if (!hasAnimated) {
          if (index === 0) {
          }
          return;
        }

        if (window.dev && !window.dev() && slideRef.current) {
          if (currentIndex !== index) {
            return;
          }
        }
      }}
      {...ar}
      aria-hidden={currentIndex !== index}
    >
      <div
        css={css`
          height: ${imgHeight}px;
          padding: ${window.dev() ? '5px' : '0px'};
        `}
      >
        <ImageZoomContext.Provider value={imageZoomContextValue}>
          {img}
        </ImageZoomContext.Provider>
      </div>

      {includeText && (
        <div
          css={css`
            position: relative;
            display: flex;
            align-items: center;
            width: 100%;
            height: ${currentIndex === index ? 'auto' : 0};

            padding: 13px 7px;
            ${window.dev() && currentIndex === index && 'padding: 3px 5px;'}

            overflow: hidden;
            word-break: break-word;

            ${currentIndex === index && 'transition: height 0.2s;'}
            transition: height 0.2s;

            * div {
              color: ${carouselTextColor};
            }

            text-align: center;

            ${defaultBulletFormatting}
          `}
        >
          {text}
        </div>
      )}
    </motion.li>
  );
};

export default CarouselSlide;
